import { useState } from "react";
import { Lock } from "lucide-react";

const EMPTY = { number: "", name: "", exp_month: "", exp_year: "", cvv: "", address1: "", city: "", state: "", zip: "", country: "BS" };

// Card fields stay in this component only, go straight to our HTTPS API (forwarded once to the card gateway), and are cleared after submit.
export function CardForm({ onSubmit, busy, submitLabel, isTest, testid = "card-form" }) {
  const [v, setV] = useState(EMPTY);
  const set = (k) => (e) => setV((s) => ({ ...s, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    const card = { number: v.number.replace(/\s/g, ""), name: v.name, exp_month: Number(v.exp_month), exp_year: Number(v.exp_year), cvv: v.cvv };
    const billing = { address1: v.address1, city: v.city, state: v.state, zip: v.zip, country: v.country.toUpperCase() };
    await onSubmit(card, billing);
    setV((s) => ({ ...s, number: "", cvv: "" }));
  };
  const f = (k, label, props = {}, cls = "sm:col-span-3") => (
    <div className={cls}><label htmlFor={`cc-${k}`} className="field-label">{label}</label>
      <input id={`cc-${k}`} className="field-input" value={v[k]} onChange={set(k)} required data-testid={`card-${k}-input`} {...props} /></div>
  );
  return (
    <form onSubmit={submit} className="rounded-lg border border-slate-300 bg-white p-5" data-testid={testid} autoComplete="on">
      <p className="mb-4 flex items-center gap-2 text-sm font-semibold text-navy"><Lock size={16} aria-hidden="true" /> Secure card payment{isTest && <span className="rounded bg-gold px-2 py-0.5 text-[10px] font-bold">TEST MODE</span>}</p>
      <div className="grid gap-4 sm:grid-cols-6">
        {f("number", "Card number", { inputMode: "numeric", autoComplete: "cc-number", maxLength: 23, placeholder: "•••• •••• •••• ••••" }, "sm:col-span-4")}
        {f("cvv", "Security code", { inputMode: "numeric", autoComplete: "cc-csc", maxLength: 4, type: "password" }, "sm:col-span-2")}
        {f("name", "Name on card", { autoComplete: "cc-name" }, "sm:col-span-6")}
        {f("exp_month", "Expiry month (MM)", { inputMode: "numeric", autoComplete: "cc-exp-month", maxLength: 2, placeholder: "MM" }, "sm:col-span-2")}
        {f("exp_year", "Expiry year (YYYY)", { inputMode: "numeric", autoComplete: "cc-exp-year", maxLength: 4, placeholder: "YYYY" }, "sm:col-span-2")}
        {f("zip", "Billing postal code", { autoComplete: "postal-code", required: false }, "sm:col-span-2")}
        {f("address1", "Billing address", { autoComplete: "address-line1" }, "sm:col-span-6")}
        {f("city", "City", { autoComplete: "address-level2" })}
        {f("country", "Country (2-letter)", { autoComplete: "country", maxLength: 2 })}
      </div>
      <button type="submit" className="btn-primary mt-5 w-full sm:w-auto" disabled={busy} data-testid={`${testid}-submit-btn`}>{busy ? "Processing… please wait" : submitLabel}</button>
      <p className="mt-3 text-xs text-[#4A5568]">Your card details are sent securely to the payment processor and are never stored by CIArb Bahamas.</p>
    </form>
  );
}

export const PaymentMessage = ({ payment }) => {
  if (!payment) return null;
  const ok = payment.status === "completed";
  const warn = ["pending_verification", "authorized"].includes(payment.status);
  return (
    <p role={ok ? "status" : "alert"} data-testid="card-payment-message"
      className={`mt-4 rounded-lg border p-4 text-sm font-semibold ${ok ? "border-green-300 bg-green-50 text-green-800" : warn ? "border-amber-300 bg-amber-50 text-amber-900" : "border-red-200 bg-red-50 text-red-800"}`}>
      {payment.message}{payment.card_last4 ? ` (card ending ${payment.card_last4})` : ""}
    </p>
  );
};
